<template>
  <div class="list-webhooks">
    <div v-if="hasWebhooks">
      <div class="d-flex justify-content-between align-items-center i-mb-14">
        <div>
          <settings-section-title id="api" class="i-mb-2"
            >Webhooks</settings-section-title
          >

          <p class="i-text-body-2 i-text-neutral-110 i-mr-6">
            Get notified automatically when a selected event is completed.
          </p>
        </div>
        <i-button
          data-testid="open-create-webhook"
          text="Create Webhook"
          size="md"
          @button-click="redirectToCreateWebhook()"
        ></i-button>
      </div>
      <div class="i-mt-14">
        <div class="w-100">
          <b-table
            id="webhooks-table"
            data-testid="webhooks-table"
            :fields="fields"
            :show-empty="false"
            hover
            :items="webhooks"
            class="i-table"
            selectable
            :per-page="perPage"
            :current-page="currentPage"
            :responsive="true"
            @row-clicked="redirectToEditWebhook"
          >
            <template #cell(url)="{ item: webhook }">
              <router-link
                :to="{
                  name: 'webhooks-settings-edit',
                  params: { webhookId: webhook.id },
                }"
              >
                <span class="i-text-subheading-2 i-text-neutral-120">
                  {{ webhook.url }}
                </span>
              </router-link>
            </template>
            <template #cell(type)="{ item: webhook }">
              <span class="i-text-body-2 i-text-neutral-120">
                {{ webhookTypeDisplayName(webhook) }}
              </span>
            </template>
            <template #cell(created_at)="{ item: webhook }">
              <span class="i-text-body-2 i-text-neutral-120">
                {{ formatDateTime(webhook.created_at, 'lll') }}
              </span>
            </template>
            <template #cell(enabled)="{ item: webhook }">
              <div class="d-flex align-items-center justify-content-between">
                <div class="i-pill i-text-subheading-3">
                  <span v-if="webhook.enabled" class="success">Enabled</span>
                  <span v-else class="neutral">Deactivated</span>
                </div>
              </div>
            </template>
            <template #cell(action)="{ item: webhook, index: index }">
              <action-menu
                :item="webhook"
                :actions="actionButtons"
                :bottom-row="bottomRow(index)"
              ></action-menu>
            </template>
          </b-table>
          <table-pagination
            v-if="showPagination"
            table-id="webhooks-table"
            :total-rows="webhooksLength"
            :per-page="perPage"
            :current-page="currentPage"
            @page-change="(page) => (currentPage = page)"
          >
          </table-pagination>
          <i-modal
            modal-id="delete-webhook"
            data-testid="delete-webhook-modal"
            title="Delete Webhook"
            confirm-button-text="Delete"
            type="error"
            @on-submit="deleteWebhook"
          >
            <div slot="body-content">
              Are you sure you want to delete this Webhook?<br />You cannot undo
              this action.
            </div>
          </i-modal>
        </div>
      </div>
    </div>
    <empty-page
      v-else-if="!loadingWebhooks"
      data-testid="webhooks-empty"
      icon="large-browser-notification"
      title="Start by creating a webhook"
      message="Get notified automatically when a selected event is completed."
      btn-text="Create Webhook"
      :btn-click="redirectToCreateWebhook"
    ></empty-page>
  </div>
</template>

<script>
import { mapState } from 'vuex';

import { iButton, iModal, openModal } from '@inscribe/signal';
import EmptyPage from '@/shared/components/EmptyPage.vue';
import SettingsSectionTitle from '../shared/SettingsSectionTitle';
import {
  deprecatedWebhookTypes,
  webhookTypes,
} from '@/V2/settings/webhooks/WebhookTypes';
import ActionMenu from '@/shared/components/ActionMenu';
import TablePagination from '@/shared/components/TablePagination';
import { formatDateTime } from '@/utils/date';

export default {
  components: {
    'action-menu': ActionMenu,
    'empty-page': EmptyPage,
    'table-pagination': TablePagination,
    iButton,
    iModal,
    'settings-section-title': SettingsSectionTitle,
  },
  data() {
    return {
      loadingWebhooks: false,
      deleteSelectedWebhook: null,
      perPage: 8,
      currentPage: 1,
      fields: [
        {
          key: 'url',
          label: 'URL',
          sortable: true,
          thClass: 'url-column',
        },
        {
          key: 'type',
          label: 'Event',
          sortable: true,
          thClass: 'event-column',
        },
        {
          key: 'created_at',
          label: 'Created',
          sortable: true,
          thClass: 'created-at-column',
        },
        {
          key: 'enabled',
          label: 'Status',
          sortable: true,
          thClass: 'status-column',
        },
        {
          key: 'action',
          label: '',
          thClass: 'action-column',
        },
      ],
      actionButtons: [
        {
          text: 'Edit',
          icon: 'small-edit-pencil',
          onClick: this.redirectToEditWebhook,
        },
        {
          text: 'Delete',
          icon: 'small-delete-circle',
          destructive: true,
          onClick: (webhook) => {
            this.deleteSelectedWebhook = webhook;
            openModal('delete-webhook');
          },
        },
      ],
      webhookTypes: [...webhookTypes, ...deprecatedWebhookTypes],
    };
  },
  computed: {
    ...mapState(['webhooks']),
    webhooksLength() {
      return this.webhooks.length;
    },
    hasWebhooks() {
      return this.webhooksLength > 0;
    },
    showPagination() {
      return this.webhooksLength > this.perPage;
    },
  },
  mounted() {
    this.loadingWebhooks = true;
    this.$store.dispatch('getWebhooks').finally(() => {
      this.loadingWebhooks = false;
    });
  },
  methods: {
    formatDateTime,
    bottomRow(rowindex) {
      return rowindex >= this.perPage - 2;
    },
    webhookTypeDisplayName(webhook) {
      return this.webhookTypes.find((type) => {
        return type.value === webhook.type;
      }).text;
    },
    redirectToCreateWebhook() {
      this.$router.push({ name: 'webhooks-settings-create' });
    },
    redirectToEditWebhook(webhook) {
      this.$router.push({
        name: 'webhooks-settings-edit',
        params: { webhookId: webhook.id },
      });
    },
    deleteWebhook() {
      this.$store
        .dispatch('deleteWebhook', this.deleteSelectedWebhook)
        .then(() => {
          this.$store.commit('deleteGeneric', {
            objectsName: 'webhooks',
            idToDelete: this.deleteSelectedWebhook.id,
          });
          this.$notify({
            group: 'api',
            type: 'success',
            duration: 5000,
            text: 'Webhook deleted',
          });
        })
        .catch(() => {
          this.$notify({
            group: 'api',
            type: 'error',
            title: 'Error',
            duration: 5000,
            text: 'Deleting Webhook Failed',
          });
        })
        .finally(() => {
          this.deleteSelectedWebhook = null;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .i-table {
  .url-column {
    width: auto;
  }
  .event-column {
    width: 325px;
  }
  .created-at-column {
    width: 200px;
  }
  .status-column {
    width: 120px;
  }
  .action-column {
    width: 40px;
  }
}
</style>
